import {metadaApi} from '@dms/api/base';

import {
  BulkDeleteDirectSaleItemsRequest,
  BulkDeleteDirectSaleItemsRequestSchema,
  BulkDeleteDirectSaleItemsResponse,
  BulkDeleteDirectSaleItemsResponseSchema,
  BulkReturnDirectSaleSparePartsRequest,
  BulkReturnDirectSaleSparePartsRequestSchema,
  BulkReturnDirectSaleSparePartsResponse,
  BulkReturnDirectSaleSparePartsResponseSchema,
  DeleteDirectSaleRequest,
  DeleteDirectSaleRequestSchema,
  DeleteDirectSaleResponse,
  DeleteDirectSaleResponseSchema,
  GetDirectSaleDiscountRequest,
  GetDirectSaleDiscountRequestSchema,
  GetDirectSaleDiscountResponse,
  GetDirectSaleDiscountResponseSchema,
  GetDirectSaleLastActiveByCustomerIdArg,
  GetDirectSaleLastActiveByCustomerIdResponse,
  GetDirectSaleRequest,
  GetDirectSaleRequestSchema,
  GetDirectSaleResponse,
  GetDirectSaleResponseSchema,
  PatchDirectSaleGetCurrentPriceRequest,
  PatchDirectSaleGetCurrentPriceRequestSchema,
  PatchDirectSaleGetCurrentPriceResponse,
  PatchDirectSaleGetCurrentPriceResponseSchema,
  PatchDirectSaleRequest,
  PatchDirectSaleRequestSchema,
  PatchDirectSaleResponse,
  PatchDirectSaleResponseSchema,
  PostDirectSaleRequest,
  PostDirectSaleRequestSchema,
  PostDirectSaleResponse,
  PostDirectSaleResponseSchema,
  PutDirectSaleDiscountRequest,
  PutDirectSaleDiscountRequestSchema,
  PutDirectSaleDiscountResponse,
  PutDirectSaleDiscountResponseSchema,
  GetDirectSaleLastActiveByCustomerIdArgSchema,
  GetDirectSaleLastActiveByCustomerIdResponseSchema,
  PostDirectSaleCheckoutResponse,
  PostDirectSaleCheckoutResponseSchema,
  PostDirectSaleCheckoutRequestSchema,
  GetDirectSaleCheckoutResponse,
  GetDirectSaleCheckoutRequest,
  GetDirectSaleCheckoutResponseSchema,
  GetDirectSaleCheckoutRequestSchema,
  BulkAssignMechanicResponse,
  BulkAssignMechanicRequest,
  BulkAssignMechanicRequestSchema,
  BulkAssignMechanicResponseSchema,
  PostDirectSaleCheckoutRequest,
} from './types';

export const metadaWarehouseDirectSaleApi = metadaApi.injectEndpoints({
  endpoints: (build) => ({
    getDirectSale: build.query<GetDirectSaleResponse, GetDirectSaleRequest>({
      query: (queryArg) => ({
        url: `warehouse/v1/direct-sale/${queryArg.directSaleId}`,
        method: 'GET',
      }),
      providesTags: (result, error, queryArg) => [{type: 'directSales', id: queryArg.directSaleId}],
      extraOptions: {
        requestSchema: GetDirectSaleRequestSchema,
        responseSchema: GetDirectSaleResponseSchema,
      },
    }),
    postDirectSale: build.mutation<PostDirectSaleResponse, PostDirectSaleRequest>({
      query: (queryArg) => ({
        url: `warehouse/v1/direct-sale`,
        method: 'POST',
        body: queryArg,
      }),
      invalidatesTags: ['directSales', 'directSaleLastActiveByCustomerId'],
      extraOptions: {
        requestSchema: PostDirectSaleRequestSchema,
        responseSchema: PostDirectSaleResponseSchema,
      },
    }),
    deleteDirectSale: build.mutation<DeleteDirectSaleResponse, DeleteDirectSaleRequest>({
      query: (queryArg) => ({
        url: `warehouse/v1/direct-sale/delete`,
        method: 'POST',
        body: queryArg,
      }),
      invalidatesTags: ['directSales', 'directSaleLastActiveByCustomerId'],
      extraOptions: {
        requestSchema: DeleteDirectSaleRequestSchema,
        responseSchema: DeleteDirectSaleResponseSchema,
      },
    }),
    patchDirectSale: build.mutation<PatchDirectSaleResponse, PatchDirectSaleRequest>({
      query: (queryArg) => ({
        url: `warehouse/v1/direct-sale/${queryArg.directSaleId}`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'directSales', id: queryArg.directSaleId},
        'directSaleLastActiveByCustomerId',
        'afsBillingInformation',
        'afsAllowedCustomer',
        'directSaleCheckout',
      ],
      extraOptions: {
        requestSchema: PatchDirectSaleRequestSchema,
        responseSchema: PatchDirectSaleResponseSchema,
      },
    }),
    bulkDeleteDirectSaleItems: build.mutation<
      BulkDeleteDirectSaleItemsResponse,
      BulkDeleteDirectSaleItemsRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/direct-sale/${queryArg.directSaleId}/items-delete`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'directSales', id: queryArg.directSaleId},
        {type: 'directSaleMaterialBasket'},
        {type: 'directSaleLabourBasket'},
        'afsCheckout',
        'afsPaymentsList',
        'afsPaymentById',
        'headerDynamicActions',
      ],
      extraOptions: {
        requestSchema: BulkDeleteDirectSaleItemsRequestSchema,
        responseSchema: BulkDeleteDirectSaleItemsResponseSchema,
      },
    }),
    getDirectSaleDiscount: build.query<GetDirectSaleDiscountResponse, GetDirectSaleDiscountRequest>(
      {
        query: (queryArg) => ({
          url: `warehouse/v1/direct-sale/${queryArg.directSaleId}/discount`,
          method: 'GET',
        }),
        providesTags: (result, error, queryArg) => [
          {type: 'directSaleDiscount', id: queryArg.directSaleId},
        ],
        extraOptions: {
          requestSchema: GetDirectSaleDiscountRequestSchema,
          responseSchema: GetDirectSaleDiscountResponseSchema,
        },
      }
    ),
    putDirectSaleDiscount: build.mutation<
      PutDirectSaleDiscountResponse,
      PutDirectSaleDiscountRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/direct-sale/${queryArg.directSaleId}/discount`,
        body: queryArg.body,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'directSaleDiscount', id: queryArg.directSaleId},
        {type: 'directSales', id: queryArg.directSaleId},
        'afsCheckout',
        'afsPaymentsList',
        'afsPaymentById',
      ],
      extraOptions: {
        requestSchema: PutDirectSaleDiscountRequestSchema,
        responseSchema: PutDirectSaleDiscountResponseSchema,
      },
    }),
    patchDirectSaleGetCurrentPrice: build.mutation<
      PatchDirectSaleGetCurrentPriceResponse,
      PatchDirectSaleGetCurrentPriceRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/direct-sale/${queryArg.directSaleId}/get-current-price`,
        body: queryArg.body,
        method: 'PATCH',
      }),
      extraOptions: {
        requestSchema: PatchDirectSaleGetCurrentPriceRequestSchema,
        responseSchema: PatchDirectSaleGetCurrentPriceResponseSchema,
      },
    }),
    bulkReturnDirectSaleSpareParts: build.mutation<
      BulkReturnDirectSaleSparePartsResponse,
      BulkReturnDirectSaleSparePartsRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/direct-sale/${queryArg.directSaleId}/spare-parts-return`,
        method: 'POST',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'directSales', id: queryArg.directSaleId},
        'partsRequestReturnBasket',
        'headerDynamicActions',
        'directSaleMaterialItems',
      ],
      extraOptions: {
        requestSchema: BulkReturnDirectSaleSparePartsRequestSchema,
        responseSchema: BulkReturnDirectSaleSparePartsResponseSchema,
      },
    }),
    bulkAssignMechanic: build.mutation<BulkAssignMechanicResponse, BulkAssignMechanicRequest>({
      query: (queryArg) => ({
        url: `warehouse/v1/direct-sale/${queryArg.directSaleId}/assign-mechanic`,
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: [
        'directSales',
        'directSaleMaterialItems',
        'directSaleLabourItems',
        'headerDynamicActions',
      ],
      extraOptions: {
        requestSchema: BulkAssignMechanicRequestSchema,
        responseSchema: BulkAssignMechanicResponseSchema,
      },
    }),
    getDirectSaleLastActiveByCustomerId: build.query<
      GetDirectSaleLastActiveByCustomerIdResponse,
      GetDirectSaleLastActiveByCustomerIdArg
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/direct-sale/by-customer/${queryArg.customerId}/last-active`,
        method: 'GET',
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'directSaleLastActiveByCustomerId', id: queryArg.customerId},
      ],
      extraOptions: {
        requestSchema: GetDirectSaleLastActiveByCustomerIdArgSchema,
        responseSchema: GetDirectSaleLastActiveByCustomerIdResponseSchema,
      },
    }),
    getDirectSaleCheckout: build.query<GetDirectSaleCheckoutResponse, GetDirectSaleCheckoutRequest>(
      {
        query: (queryArg) => ({
          url: `checkout/v1/direct-sale/${queryArg.directSaleId}/checkout-link`,
          method: 'GET',
          queryArg,
        }),
        providesTags: (result, error, queryArg) => [
          {type: 'directSaleCheckout', id: queryArg.directSaleId},
        ],
        extraOptions: {
          requestSchema: GetDirectSaleCheckoutRequestSchema,
          responseSchema: GetDirectSaleCheckoutResponseSchema,
        },
      }
    ),
    postDirectSaleCheckout: build.mutation<
      PostDirectSaleCheckoutResponse,
      PostDirectSaleCheckoutRequest
    >({
      query: (queryArg) => ({
        url: `checkout/v1/direct-sale/${queryArg.directSaleId}/checkout`,
        method: 'POST',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'directSaleCheckout', id: queryArg.directSaleId},
        {type: 'directSales', id: queryArg.directSaleId},
        'headerDynamicActions',
      ],
      extraOptions: {
        requestSchema: PostDirectSaleCheckoutRequestSchema,
        responseSchema: PostDirectSaleCheckoutResponseSchema,
      },
    }),
  }),
});

export const {
  useGetDirectSaleQuery,
  useLazyGetDirectSaleQuery,
  usePostDirectSaleMutation,
  usePatchDirectSaleMutation,
  useDeleteDirectSaleMutation,
  useBulkDeleteDirectSaleItemsMutation,
  useGetDirectSaleDiscountQuery,
  usePutDirectSaleDiscountMutation,
  usePatchDirectSaleGetCurrentPriceMutation,
  useBulkReturnDirectSaleSparePartsMutation,
  useBulkAssignMechanicMutation,
  useGetDirectSaleLastActiveByCustomerIdQuery,
  useGetDirectSaleCheckoutQuery,
  usePostDirectSaleCheckoutMutation,
} = metadaWarehouseDirectSaleApi;
